import React, { useState } from "react";
import { ItemToRemove } from "../../types/order";

interface RemoveItemsModalProps {
  title: string;
  items: ItemToRemove[];
  onConfirm: (selected: string[]) => void;
  onClose: () => void;
}

const RemoveItemsModal: React.FC<RemoveItemsModalProps> = ({
  title,
  items,
  onConfirm,
  onClose,
}) => {
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  const toggleSelect = (id: string) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (filteredIds: string[]) => {
    const allSelected = filteredIds.every((id) => selectedItems.includes(id));
    if (allSelected) {
      setSelectedItems((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      setSelectedItems((prev) =>
        prev.concat(filteredIds.filter((id) => !prev.includes(id)))
      );
    }
  };

  const orderItems = items.filter((i) => i.quantity !== undefined);
  const bookingItems = items.filter((i) => i.duration !== undefined);

  // Compute selected items and total dynamically
  const selectedData = items.filter((item) => selectedItems.includes(item.id));
  const selectedCount = selectedData.length;
  const selectedTotal = selectedData.reduce((sum, item) => {
    const lineTotal =
      item.quantity !== undefined ? item.amount : item.amount;
    return sum + lineTotal;
  }, 0);

  const renderTable = (label: string, data: ItemToRemove[], isOrder = true) => {
    if (data.length === 0) return null;
    const ids = data.map((d) => d.id);

    return (
      <div className="mb-6">
        <h3 className="text-lg font-medium mb-2">{label}</h3>

        <div className="overflow-y-auto max-h-64 border rounded-md">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-gray-100">
                <th className="p-2 text-left">
                  <input
                    type="checkbox"
                    checked={ids.every((id) => selectedItems.includes(id))}
                    onChange={() => toggleSelectAll(ids)}
                  />
                </th>
                <th className="p-2 text-left">Item</th>
                <th className="p-2 text-center">Price (₦)</th>
                <th className="p-2 text-center">{isOrder ? "Quantity" : "Duration"}</th>
                <th className="p-2 text-center">Total (₦)</th>
              </tr>
            </thead>
            <tbody>
              {data.map((item) => {
                const qty = item.quantity ?? 1;
                const lineTotal =
                  item.quantity !== undefined ? item.quantity * item.amount : item.amount;
                return (
                  <tr key={item.id} className="border-t hover:bg-gray-50 transition">
                    <td className="p-2">
                      <input
                        type="checkbox"
                        checked={selectedItems.includes(item.id)}
                        onChange={() => toggleSelect(item.id)}
                      />
                    </td>
                    <td className="p-2">{item.itemName}</td>
                    <td className="p-2 text-center">{(item.amount / item.quantity).toLocaleString()}</td>
                    <td className="p-2 text-center">{isOrder ? item.quantity : item.duration}</td>
                    <td className="p-2 text-center">{(item.amount).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50">
      <div className="bg-white w-full max-w-2xl rounded-lg shadow-lg p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-2 text-center">{title}</h2>

        {/* Subheading / instructions */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-gray-600">
            Select items to remove. Use the checkboxes or the <strong>Select All</strong> per section.
          </p>
          <div className="text-sm font-medium text-gray-700">
            Selected: {selectedCount} • ₦{selectedTotal.toLocaleString()}
          </div>
        </div>

        {/* Orders */}
        {renderTable("Orders", orderItems, true)}

        {/* Bookings */}
        {renderTable("Bookings", bookingItems, false)}

        <div className="flex justify-end gap-3 mt-4">
          <button
            className="px-4 py-2 rounded-md bg-gray-200 hover:bg-gray-300"
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            className="px-4 py-2 rounded-md bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
            onClick={
              () => onConfirm(selectedItems)
            }
            disabled={selectedItems.length === 0}
          >
            Remove Selected
          </button>
        </div>
      </div>
    </div>
  );
};

export default RemoveItemsModal;